import { useEffect } from 'react';
import { Control, FieldPath, FieldValues, useFormState, useWatch, useFormContext } from 'react-hook-form';
import { MaterialProperties, ThicknessOptions } from '@steelbuy/ts-shared';
import { NumberInput } from '../primitive/number-input/NumberInput';
import './RFQThicknessInput.scss';

interface RFQThicknessInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
    material: MaterialProperties;
    // eslint-disable-next-line react/no-unused-prop-types
    name: TName;
    control: Control<TFieldValues>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rules?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rulesMin?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rulesMax?: any;
}

export const RFQThicknessInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    material,
    control,
    rules,
    rulesMin,
    rulesMax,
}: RFQThicknessInputProps<TFieldValues, TName>) => {
    const isRange = material.thicknessOptions === ThicknessOptions.RANGE;

    const { trigger } = useFormContext<TFieldValues>();

    // Read touched/error state without registering the fields — using useController
    // would keep thicknessMin/thicknessMax registered even in SINGLE mode, blocking isValid.
    const { touchedFields, errors } = useFormState<TFieldValues>({ control });
    const typedTouched = touchedFields as Record<string, boolean>;
    const typedErrors = errors as Record<string, { type?: string; message?: string }>;

    const thicknessMinValue = useWatch({ control, name: 'thicknessMin' as FieldPath<TFieldValues> });
    const thicknessMaxValue = useWatch({ control, name: 'thicknessMax' as FieldPath<TFieldValues> });

    // Re-validate the sibling field when either value changes so cross-field
    // errors (min >= max) clear as soon as the user fixes the other input.
    useEffect(() => {
        if (isRange) {
            trigger('thicknessMin' as FieldPath<TFieldValues>);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thicknessMaxValue, isRange]);

    useEffect(() => {
        if (isRange) {
            trigger('thicknessMax' as FieldPath<TFieldValues>);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thicknessMinValue, isRange]);

    // Show errors once the field has been touched, or if it already has a value
    // (e.g. pre-populated in edit mode — reset() doesn't mark fields as touched).
    // The cross-field validators (mustBeLessThan / mustBeGreaterThan) only fire when
    // the sibling field has a real value, so no extra gating is needed here.
    const minError = typedTouched.thicknessMin || thicknessMinValue ? typedErrors.thicknessMin?.message : undefined;
    const maxError = typedTouched.thicknessMax || thicknessMaxValue ? typedErrors.thicknessMax?.message : undefined;

    if (isRange) {
        return (
            <div className="rfq-thickness-range">
                <NumberInput
                    name={'thicknessMin' as TName}
                    control={control}
                    rules={rulesMin}
                    error={minError}
                    controlledError
                    endAdornment="Min"
                />
                <NumberInput
                    name={'thicknessMax' as TName}
                    control={control}
                    rules={rulesMax}
                    error={maxError}
                    controlledError
                    endAdornment="Max"
                />
            </div>
        );
    }

    return <NumberInput name={'thickness' as TName} control={control} rules={rules} />;
};
